import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Spinner } from '../spinner/spinner'; 
import { isEphemeralIndex } from '../interfaces';

export interface ViewItem {
  id: string;
  indexName?: string;
  name?: string;
  url?: string;
  title?: string;
  type: 'index' | 'site' | 'page';
  status?: string;
  isFolder: boolean;
  originalNode?: any;
}

export interface Breadcrumb {
  name: string;
  level: 'index' | 'site';
  node: any;
}

@Component({
  selector: 'app-web-table',
  standalone: true,
  imports: [CommonModule, Spinner],
  templateUrl: './web-table-component.html',
  styleUrls: ['./web-table-component.scss']
})
export class WebTableComponent {
  
  private _targetIndex: string = '';

  @Input() set targetIndex(val: string) {
    this._targetIndex = val || '';
    if (this.rawData().length > 0 || this._targetIndex) {
      this.applyInitialLevel();
    }
  }
  get targetIndex(): string {
    return this._targetIndex;
  }

  @Input() set data(val: any[]) {
    this.rawData.set(val || []);
    this.applyInitialLevel();
  }
  @Input() subtitle = signal<string>('Naviga tra i siti scansionati');
  
  @Input() isLoading: boolean = false;
  @Input() isProcessing: boolean = false;
  @Input() hasError: boolean = false;

  @Output() infoActionClicked = new EventEmitter<any>();
  @Output() activeIndexChanged = new EventEmitter<string | null>();

  rawData = signal<any[]>([]);
  currentLevel = signal<'root' | 'index' | 'site'>('root');
  visibleItems = signal<ViewItem[]>([]);
  breadcrumb = signal<Breadcrumb[]>([]);

  applyInitialLevel() {
    if (this._targetIndex) {
      const node = this.rawData().find((idx) => idx.indexName === this._targetIndex) || { indexName: this._targetIndex, sites: [] };
      this.buildIndexLevel(node);
    } else {
      this.buildRootLevel();
    }
  }

  // Livello 1: Mostra tutti gli Indici
  buildRootLevel() {
    this.currentLevel.set('root');
    this.breadcrumb.set([]);
    this.activeIndexChanged.emit(null);
    
    // Sottotitolo quando sei alla radice
    this.subtitle.set('Seleziona un indice per visualizzare i siti');
    
    const items: ViewItem[] = this.rawData()
      .filter((idx) => !isEphemeralIndex({ name: idx.indexName, isEphemeral: idx.isEphemeral }))
      .map((idx) => ({
        id: idx.indexName, indexName: idx.indexName, name: idx.indexName,
        type: 'index', isFolder: true, originalNode: idx
      }));
    this.visibleItems.set(items);
  }

  // Livello 2: Mostra i Siti dentro un Indice
  buildIndexLevel(indexNode: any) {
    this.currentLevel.set('index');
    this.breadcrumb.set([{ name: indexNode.indexName, level: 'index', node: indexNode }]);
    this.activeIndexChanged.emit(indexNode.indexName);
    
    this.subtitle.set(`Indice: ${indexNode.indexName}`);
    
    const items: ViewItem[] = (indexNode.sites || []).map((site: any) => ({
      id: site.id, url: site.url, status: site.status,
      type: 'site', isFolder: true, originalNode: site
    }));
    this.visibleItems.set(items);
  }

  // Livello 3: Mostra le Pagine dentro un Sito
  buildSiteLevel(siteNode: any) {
    this.currentLevel.set('site');
    const parentIndex = this.rawData().find(idx => (idx.sites || []).some((s:any) => s.id === siteNode.id));
        
    this.breadcrumb.set([
      { name: parentIndex?.indexName || 'Indice', level: 'index', node: parentIndex },
      { name: siteNode.url, level: 'site', node: siteNode }
    ]);
    this.activeIndexChanged.emit(null);
    
    const items: ViewItem[] = (siteNode.pages || []).map((page: any) => ({
      id: page.id, url: page.url, title: page.title, status: page.status,
      type: 'page', isFolder: false, originalNode: page
    }));
    this.visibleItems.set(items);
  }

  goToFolder(item: ViewItem) {
    if (item.type === 'index') this.buildIndexLevel(item.originalNode);
    else if (item.type === 'site') this.buildSiteLevel(item.originalNode);
  }

  goToBreadcrumb(crumb: Breadcrumb) {
    if (crumb.level === 'index') this.buildIndexLevel(crumb.node);
    else if (crumb.level === 'site') this.buildSiteLevel(crumb.node);
  }

  goBack() {
    const crumbs = this.breadcrumb();
    if (crumbs.length === 2) {
      this.buildIndexLevel(crumbs[0].node);
    } else if (this._targetIndex) {
      this.applyInitialLevel();
    } else {
      this.buildRootLevel();
    }
  }
  
  goToRoot() {
    if (this._targetIndex) {
      this.applyInitialLevel();
    } else {
      this.buildRootLevel();
    }
  }
}